import { Injectable } from '@angular/core';
import { AuthenticationResult } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { WarningType, ApproveStatus } from '@shared/enums/dpr';
import { UserSession } from '@shared/models';
import {
  Attachment,
  DprActivity,
  DprApprover,
  DprConsumable,
  DprDetails,
  DprDetailsSync,
  DprHidden,
  DprForecastActivity,
  DprHse,
  DprOnlineApproval,
  DprPersonnel,
  DprProgress,
  DprSummary,
  DprWeather,
  DprAttachment,
  DprStatusTracker,
  DprTableOfColumns,
  DprActivitiesDurationHistory,
  DprProgressWithoutTarget,
  DprFreeTextField,
} from '@shared/models/dpr';
import { OfflineState } from '..';
import { OfflineActions } from '../actions';
import { offlineQuery } from '../offline.selector';

@Injectable()
export class OfflineFacade {
  query = {
    dprDetailsList$: this.store.select(offlineQuery.getDprDetailsList),
    dprDetails$: this.store.select(offlineQuery.getDprDetails),
    combinedDprDetailsList$: this.store.select(
      offlineQuery.getCombinedDprDetailsList
    ),
    backupUserDetails$: this.store.select(offlineQuery.getBackupUserDetails),
    backupLastDprs$: this.store.select(offlineQuery.getBackupLastDprs),
    backupAuthenticationResult$: this.store.select(
      offlineQuery.getBackupAuthenticationResult
    ),
    dprDetailsOfflineChanges$: this.store.select(
      offlineQuery.getDprDetailsOfflineChanges
    ),
    syncOfflineGuid$: this.store.select(offlineQuery.getSyncOfflineGuid),
    mostRecentDprDetails$: this.store.select(
      offlineQuery.getMostRecentDprDetails
    ),
  };

  constructor(private store: Store<OfflineState>) {}

  setDprDetailsList(dprDetailsList: DprDetails[]): void {
    this.store.dispatch(OfflineActions.setDprDetailsList({ dprDetailsList }));
  }

  setDprDetails(dprDetails: DprDetails): void {
    this.store.dispatch(OfflineActions.setDprDetails({ dprDetails }));
  }

  updateExistingDprDetails(dprDetails: DprDetails): void {
    this.store.dispatch(
      OfflineActions.updateExistingDprDetails({ dprDetails })
    );
  }

  updateDprDetails(
    oldDprDetailId: number | string,
    dprDetails: DprDetails
  ): void {
    this.store.dispatch(
      OfflineActions.updateDprDetails({ oldDprDetailId, dprDetails })
    );
  }

  getDprDetails(dprDetailId: number | string): Observable<DprDetails> {
    return combineLatest([
      this.query.dprDetailsList$,
      this.query.dprDetails$,
    ]).pipe(
      map(([dprDetailsList, dprDetails]) => {
        // double equal needed, because in offline the number typed ids are also sent as strings (in double quote marks)
        // eslint-disable-next-line eqeqeq
        if (dprDetails?.id == dprDetailId) {
          return dprDetails;
        }

        // eslint-disable-next-line eqeqeq
        return dprDetailsList?.find((x) => x.id == dprDetailId);
      })
    );
  }

  setBackupUserDetails(backupUserDetails: UserSession): void {
    this.store.dispatch(
      OfflineActions.setBackupUserDetails({ backupUserDetails })
    );
  }

  setBackupLastDprs(backupLastDprs: DprDetails[]): void {
    this.store.dispatch(OfflineActions.setBackupLastDprs({ backupLastDprs }));
  }

  setBackupAuthenticationResult(
    backupAuthenticationResult: AuthenticationResult
  ): void {
    this.store.dispatch(
      OfflineActions.setBackupAuthenticationResult({
        backupAuthenticationResult,
      })
    );
  }

  addDprDetails(dprDetails: DprDetails): void {
    this.store.dispatch(OfflineActions.addDprDetails({ dprDetails }));
  }

  setDprDetailsOfflineChanges(
    dprDetailsOfflineChanges: DprDetailsSync[]
  ): void {
    this.store.dispatch(
      OfflineActions.setDprDetailsOfflineChanges({ dprDetailsOfflineChanges })
    );
  }

  updateDprDetailsOfflineChanges(changes: DprDetailsSync): void {
    this.store.dispatch(
      OfflineActions.updateDprDetailsOfflineChanges({ changes })
    );
  }

  clearDprDetails(): void {
    this.setDprDetails(null);
    this.setDprDetailsList(null);
  }

  setDprDetailsSignedReport(
    dprDetailId: number | string,
    attachment: Attachment
  ): void {
    this.store.dispatch(
      OfflineActions.setDprDetailsSignedReport({ dprDetailId, attachment })
    );
  }

  setDprDetailsLockedDate(
    dprDetailId: number | string,
    lockedDate: Date
  ): void {
    this.store.dispatch(
      OfflineActions.setDprDetailsLockedDate({ dprDetailId, lockedDate })
    );
  }

  setDprDetailsNewRevision(
    dprDetailId: number | string,
    dprDetails: DprDetails
  ): void {
    this.store.dispatch(
      OfflineActions.setDprDetailsNewRevision({ dprDetailId, dprDetails })
    );
  }

  setDprActivityHistory(
    dprDetailId: number | string,
    dprActivitiesDurationHistory: DprActivitiesDurationHistory[]
  ): void {
    this.store.dispatch(
      OfflineActions.setDprActivitiesHistory({
        dprDetailId,
        dprActivitiesDurationHistory,
      })
    );
  }

  setDprActivities(
    dprActivities: DprActivity[],
    dprParallelActivities: DprActivity[],
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprActivities({
        dprActivities,
        dprParallelActivities,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  setDprStatusTracker(
    dprStatusTracker: DprStatusTracker,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprStatusTracker({
        dprStatusTracker,
        dprDetailId,
      })
    );
  }

  setDprWeather(dprWeather: DprWeather, dprDetailId: number | string): void {
    this.store.dispatch(
      OfflineActions.setDprWeather({
        dprWeather,
        dprDetailId,
      })
    );
  }

  setDprHse(dprHse: DprHse, dprDetailId: number | string): void {
    this.store.dispatch(
      OfflineActions.setDprHse({
        dprHse,
        dprDetailId,
      })
    );
  }

  setDprPersonnel(
    dprPersonnel: DprPersonnel,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprPersonnel({
        dprPersonnel,
        dprDetailId,
      })
    );
  }

  setDprProgress(dprProgress: DprProgress, dprDetailId: number | string): void {
    this.store.dispatch(
      OfflineActions.setDprProgress({
        dprProgress,
        dprDetailId,
      })
    );
  }

  setDprProgressWithoutTarget(
    dprProgressWithoutTarget: DprProgressWithoutTarget,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprProgressWithoutTarget({
        dprProgressWithoutTarget,
        dprDetailId,
      })
    );
  }

  setDprConsumable(
    dprConsumable: DprConsumable,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprConsumable({
        dprConsumable,
        dprDetailId,
      })
    );
  }

  setDprSummary(
    dprSummary: DprSummary,
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprSummary({
        dprSummary,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  setDprForecastActivities(
    dprForecastActivities: DprForecastActivity[],
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprForecastActivities({
        dprForecastActivities,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  setDprFreeTextField(
    dprFreeTextField: DprFreeTextField,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprFreeTextField({
        dprFreeTextField,
        dprDetailId,
      })
    );
  }

  setDprSummaryExternalComment(
    dprSummary: DprSummary,
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprSummaryExternalComment({
        dprSummary,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  setDprTableOfColumns(
    dprTableOfColumn: DprTableOfColumns,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprTableOfColumns({
        dprTableOfColumn,
        dprDetailId,
      })
    );
  }

  setDprAppendix(
    dprAppendixAttachments: DprAttachment[],
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprAppendix({
        dprAppendixAttachments,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  setDprHidden(
    dprHidden: DprHidden[],
    firstWarning: WarningType,
    warningStatusTab: string,
    dprDetailId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.setDprHidden({
        dprHidden,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      })
    );
  }

  addDprApprover(dprDetailId: number | string, dprApprover: DprApprover): void {
    this.store.dispatch(
      OfflineActions.addDprApprover({
        dprDetailId,
        dprApprover,
      })
    );
  }

  updateDprApprover(
    dprDetailId: number | string,
    dprApprover: DprApprover
  ): void {
    this.store.dispatch(
      OfflineActions.updateDprApprover({
        dprDetailId,
        dprApprover,
      })
    );
  }

  deleteDprApprover(
    dprDetailId: number | string,
    dprApproverId: number | string
  ): void {
    this.store.dispatch(
      OfflineActions.deleteDprApprover({
        dprDetailId,
        dprApproverId,
      })
    );
  }

  updateDprOnlineApproval(
    dprDetailId: number | string,
    dprOnlineApproval: DprOnlineApproval
  ): void {
    this.store.dispatch(
      OfflineActions.updateDprOnlineApproval({
        dprDetailId,
        dprOnlineApproval,
      })
    );
  }

  updateDprApproveStatus(
    dprDetailId: number | string,
    approveStatus: ApproveStatus
  ): void {
    this.store.dispatch(
      OfflineActions.updateDprApproveStatus({
        dprDetailId,
        approveStatus,
      })
    );
  }

  setSyncOfflineGuid(): void {
    this.store.dispatch(OfflineActions.setSyncOfflineGuid());
  }

  deleteSyncOfflineGuid(): void {
    this.store.dispatch(OfflineActions.deleteSyncOfflineGuid());
  }
}
